import React from 'react'
import { Button, Dialog, DialogActions, DialogContent, Divider, Fab, Stack, Typography, styled } from '@mui/material'
import MapIcon from '@mui/icons-material/Map'
import AddIcon from '@mui/icons-material/Add'

import dayjs from 'dayjs'
import { useGlobalState } from 'state'
import { amber, grey } from '@mui/material/colors'
import { isPrivateMode, isPublicMode, isYosokuMode } from 'common/config'
import { MapStyles, MenuItems, manualURL } from 'common/consts'
import { MenuType } from 'common/types'
import { DateTimePicker } from '@mui/x-date-pickers'
import UpdateIcon from '@mui/icons-material/Update'
import LocationIcon from '@mui/icons-material/LocationOn'
import { HazardMaps } from 'common/hazardmaps'

export interface MobileMenuDialogProps {
  open: boolean;
  onClose: () => void;
}

const TitleLabel = ({label}: {label: string}) => {
  return (
    <Typography variant='caption' sx={{mt: '6px', fontSize: '11px', fontWeight: 'bold', color: grey[700]}}>
      {label}
    </Typography>
  )
}

const MenuButton = ({label, selected, icon, onClick = () => {}}: {
  label: string,
  selected: boolean,
  icon?: React.ReactNode,
  onClick?: () => void
}) => {
  const arr = label.split('\n')
  const label1 = arr.length <= 0 ? '' : arr[0]
  const label2 = arr.length <= 1 ? '' : arr[1]
  return (
    <Button
      variant={selected ? 'contained' : 'outlined'}
      size="small"
      startIcon={icon}
      sx={{
        width: 126,
        whiteSpace: 'pre',
        lineHeight: 1.2,
        fontSize: '10px',
        minHeight: 32,
        justifyContent: 'left',
        fontWeight: selected ? 'bold' : 'normal',
        p: '4px 0 4px 6px'
      }}
      onClick={onClick}>
      <Stack alignItems={'flex-start'}>
        <Typography variant="inherit" noWrap>{label1}</Typography>
        { label2 && <Typography variant="inherit" noWrap>{label2}</Typography> }
      </Stack>
    </Button>
  )
}

const NowFab = styled(Fab)(() => ({
  backgroundColor: amber[600],
  color: grey[800],
  width: 36,
  height: 36,
  minHeight: 36,
  '&:hover': {
    backgroundColor: amber[500]
  }
}))

export const MobileMenuDialog = ({open, onClose}: MobileMenuDialogProps) => {
  const state = useGlobalState()
  const currentStyleId = React.useMemo(() => state.map.style.id, [state.map.style])
  const currentMenuId = React.useMemo(() => state.app.menu, [state.app.menu])
  const baseDate = React.useMemo(() => state.app.baseDate.add(9, 'hours'), [state.app.baseDate])
  const hazardMapIds = React.useMemo(() => state.app.hazardMapIds, [state.app.hazardMapIds])

  const handleClose = () => {
    onClose()
  }

  const selectMenu = (menu: {id: string, name: string, url?: string}) => {
    if (menu.url) {
      window.open(menu.url, '_blank')
    } else {
      if (currentMenuId !== menu.id as MenuType) {
        state.app.setMenu(menu.id as MenuType)
      } else {
        state.app.setMenu('')
      }
      onClose()
    }
  }

  const selectHazardMap = (targetId: string) => {
    if (hazardMapIds.includes(targetId)) {
      state.app.setHazardMapIds(hazardMapIds.filter(id => id != targetId))
    } else {
      state.app.setHazardMapIds([...hazardMapIds, targetId])
    }
  }

  const getMinDateTime = () => {
    if (isPrivateMode) return undefined
    const date = dayjs.utc().subtract(1, 'month')
    const limit = dayjs.utc('2025/05/20 00:00', 'YYYY/MM/DD HH:mm')
    if (date < limit) {
      return limit
    } else {
      return date.subtract(date.hour(), 'hour').subtract(date.minute(), 'minute').subtract(date.second(), 'second')
    }
  }

  return (
    <Dialog onClose={handleClose} open={open}>
      <DialogContent sx={{p: '20px 4px 12px 10px', maxWidth: 280}}>
        <Stack>
          {!isYosokuMode && (
            <Stack direction='row' spacing={'8px'} alignItems={'center'}>
              <DateTimePicker
                value={baseDate}
                localeText={{toolbarTitle: '日時設定', cancelButtonLabel: 'キャンセル'}}
                minDateTime={getMinDateTime()}
                maxDateTime={dayjs.utc().add(9, 'hours')}
                reduceAnimations
                sx={{width: 210}}
                slotProps={{ textField: { size: 'small', label: '日時' }, toolbar: { hidden: true } }}
                onChange={d => d !== null && state.app.setBaseDate(d.subtract(9, 'hours'))}
              />
              <NowFab onClick={() => state.app.setBaseDate(dayjs.utc())}>
                <UpdateIcon />
              </NowFab>
            </Stack>
          )}
          { isPrivateMode && (
            <>
              <TitleLabel label={'表示データ'} />
              <Stack direction='row' spacing={'6px'} useFlexGap flexWrap="wrap">
                {
                  MenuItems.filter(menu => menu.visible === true).map(menu => <MenuButton
                    key={menu.id}
                    label={menu.name.replace('リスクライン', 'ﾘｽｸﾗｲﾝ').replace('リスク', 'ﾘｽｸ')}
                    selected={menu.id === currentMenuId}
                    icon={menu.icon}
                    onClick={() => selectMenu(menu)} />
                  )
                }
              </Stack>
            </>
          )}
          { isPublicMode && (
            <Stack my={2}>
              <MenuButton
                label={'操作マニュアル'}
                selected={false}
                icon={<LocationIcon />}
                onClick={() => window.open(manualURL, '_blank')}
              />
            </Stack>
          )}

          { isPublicMode && (
            <>
              <TitleLabel label={'自宅等の地点登録'} />
              <Stack spacing={state.app.mobileLocations.length > 0 ? 1 : 0} mb={1}>
                <Stack direction='row' spacing={'6px'} useFlexGap flexWrap="wrap">
                  { state.app.mobileLocations.map((obs, i) => (
                    <MenuButton
                      key={i}
                      label={obs.name || `位置${i + 1}`}
                      selected={false}
                      icon={<LocationIcon />}
                      onClick={() => {
                        state.app.setMobileLocationAddMode(false)
                        state.map.setView(obs.lng, obs.lat, 15)
                        handleClose()
                      }} />
                  )) }
                </Stack>
                <MenuButton
                  label={'新規登録'}
                  icon={<AddIcon />}
                  selected={false}
                  onClick={() => {
                    state.app.setMobileLocationAddMode(true)
                    handleClose()
                  }} />
              </Stack>
            </>
          )}

          <TitleLabel label={'地図'} />
          <Stack direction='row' spacing={'6px'} useFlexGap flexWrap="wrap">
            {
              MapStyles.map(s => <MenuButton
                key={s.id}
                label={s.name}
                selected={s.id === currentStyleId}
                icon={<MapIcon />}
                onClick={() => state.map.setStyle(s)} />
              )
            }
          </Stack>

          { isPrivateMode && (
            <>
              <TitleLabel label={'ハザードマップ'} />
              <Stack direction='row' spacing={'6px'} useFlexGap flexWrap="wrap">
                {
                  HazardMaps.map(s => <MenuButton
                    key={s.id}
                    label={s.label}
                    selected={hazardMapIds.includes(s.id)}
                    onClick={() => selectHazardMap(s.id)} />
                  )
                }
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <Divider sx={{mx: 2 }} />
      <DialogActions sx={{py: 0}}>
        <Button onClick={handleClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  )
}
